import type React from "react";
import {
  Building2,
  Users,
  Calendar,
  CreditCard,
  X,
  Briefcase,
  Target,
  CheckCircle2,
  Package,
  TrendingUp,
  TrendingDown,
  BarChart,
} from "lucide-react";
import type { Company } from "./types";

interface CompanyDetailsModalProps {
  company: Company;
  isOpen: boolean;
  onClose: () => void;
}

const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({
  company,
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  // Handle the hyphenated property safely
  const companyAny = company as any;
  const achievedVisits =
    companyAny["achieved-visits"]?.total_reports ||
    company.achieved_visits?.total_reports ||
    0;
  const plannedVisits = company.planned_visits?.total_visits || 0;

  // New achieved-visits data
  const currentWeekTotal =
    companyAny["achieved-visits"]?.current_week_total || 0;
  const percentageChange =
    companyAny["achieved-visits"]?.percentage_change || 0;
  const previousWeekTotal =
    companyAny["achieved-visits"]?.previous_week_total || 0;

  const callRate = Number(company.call_rate);
  const formattedCallRate = isNaN(callRate)
    ? "0%"
    : callRate === 0
    ? "0"
    : `${callRate.toFixed(2)}%`;

  // Format amount in Naira
  const formatNairaAmount = (amount: string | number | undefined) => {
    if (amount === undefined || amount === null) return "USD0.00";
    const numAmount =
      typeof amount === "string" ? Number.parseFloat(amount) : amount;
    return `USD${numAmount.toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Customer targets data
  const customerTargets = company.customers_target || {
    category_breakdown: { A: 0, B: 0, C: 0 },
    total_customers: 0,
    total_target: 0,
  };

  // Customers info data
  const customersInfo = company.customers_info || {
    category_breakdown: { A: 1, B: 1, C: 1 },
    total_customers: 3,
  };

  // Fixed company targets data

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b p-6">
          <div className="flex items-center gap-4">
            {company.file_path ? (
              <img
                src={company.file_path || "/placeholder.svg"}
                alt={`${company.name} logo`}
                className="w-14 h-14 rounded-full object-cover border-2 border-gray-100"
              />
            ) : (
              <div className="w-14 h-14 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xl font-semibold">
                {company.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {company.name}
              </h2>
              <p className="text-gray-500 flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                ID: {company.customID}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Info Section */}
          <div className="col-span-2 bg-gray-50/50 border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
              <Building2 className="w-5 h-5 text-primary" />
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-gray-500 text-sm mb-1">Company Name</p>
                <p className="font-medium text-gray-900">{company.name}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Company ID</p>
                <p className="font-medium text-gray-900">{company.customID}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Call Rate</p>
                <p className="font-medium text-gray-900">{formattedCallRate}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Total Customers</p>
                <p className="font-medium text-gray-900">
                  {company.customers?.customerAnalytics?.total_customers || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Company Targets Section */}
          <div className="bg-gray-50/50 border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
              <BarChart className="w-5 h-5 text-primary" />
              Company Targets
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Category A Target</p>
                <p className="font-medium text-gray-900">
                  {company.targets?.A || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Category B Target</p>
                <p className="font-medium text-gray-900">
                  {company.targets?.B || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Category C Target</p>
                <p className="font-medium text-gray-900">
                  {company.targets?.C || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Targets Section */}
          <div className="bg-gray-50/50 border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
              <Users className="w-5 h-5 text-primary" />
              Customer Targets
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Total Target</p>
                <p className="font-medium text-gray-900">
                  {customerTargets.total_target}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Category A</p>
                <p className="font-medium text-gray-900">
                  {customerTargets.category_breakdown?.A || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Category B</p>
                <p className="font-medium text-gray-900">
                  {customerTargets.category_breakdown?.B || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Category C</p>
                <p className="font-medium text-gray-900">
                  {customerTargets.category_breakdown?.C || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Customers Info Section */}
          <div className="bg-gray-50/50 border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
              <Users className="w-5 h-5 text-primary" />
              Customers Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Total Customers</p>
                <p className="font-medium text-gray-900">
                  {customersInfo.total_customers}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">
                  Category A Customers
                </p>
                <p className="font-medium text-gray-900">
                  {customersInfo.category_breakdown?.A || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">
                  Category B Customers
                </p>
                <p className="font-medium text-gray-900">
                  {customersInfo.category_breakdown?.B || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">
                  Category C Customers
                </p>
                <p className="font-medium text-gray-900">
                  {customersInfo.category_breakdown?.C || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Visit Information */}
          <div className="bg-gray-50/50 border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
              <Target className="w-5 h-5 text-primary" />
              Visit Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Planned Visits</p>
                <p className="font-medium text-gray-900">{plannedVisits}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Achieved Visits</p>
                <p className="font-medium text-gray-900">{achievedVisits}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">
                  Current Week Visits
                </p>
                <p className="font-medium text-gray-900">{currentWeekTotal}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">
                  Previous Week Visits
                </p>
                <p className="font-medium text-gray-900">{previousWeekTotal}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Weekly Change</p>
                <p className="font-medium flex items-center gap-1">
                  {percentageChange > 0 ? (
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  ) : percentageChange < 0 ? (
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  ) : null}
                  <span
                    className={
                      percentageChange > 0
                        ? "text-green-600"
                        : percentageChange < 0
                        ? "text-red-600"
                        : "text-gray-900"
                    }
                  >
                    {percentageChange > 0 ? "+" : ""}
                    {percentageChange}%
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Staff Information */}
          <div className="bg-gray-50/50 border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
              <Users className="w-5 h-5 text-primary" />
              Staff Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Managers</p>
                <p className="font-medium text-gray-900">
                  {company.managers || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">
                  Sales Representatives
                </p>
                <p className="font-medium text-gray-900">
                  {company.salesReps || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Package Section */}
          <div className="bg-gray-50/50 border rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
              <CreditCard className="w-5 h-5 text-primary" />
              Payment Information
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Payment Status</p>
                <p className="font-medium text-gray-900 flex items-center gap-1.5">
                  <CheckCircle2
                    className={`w-4 h-4 ${
                      company.paid_status === "paid"
                        ? "text-green-500"
                        : "text-gray-400"
                    }`}
                  />
                  {company.paid_status || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Package</p>
                <p className="font-medium text-gray-900 flex items-center gap-1.5">
                  <Package className="w-4 h-4 text-primary" />
                  {company.payment_package?.payment_package || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Amount</p>
                <p className="font-medium text-gray-900">
                  {formatNairaAmount(company.payment_package?.amount)}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Expiry Date</p>
                <p className="font-medium text-gray-900 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-primary" />
                  {company.payment_package?.expiry_date || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="border-t p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsModal;
